import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { ApiError } from '../../api/generated/core/ApiError';
import { CancelError, type CancelablePromise } from '../../api/generated/core/CancelablePromise';
import { ContentMasterService } from '../../api/generated/services/ContentMasterService';
import { submitMarketplaceListingGenerate } from './marketplaceListingSlice';
import type { ContentMasterProductListView } from '../../api/generated/models/ContentMasterProductListView';
import type { ContentMasterLocalProductListView } from '../../api/generated/models/ContentMasterLocalProductListView';
import type { ContentMasterLocalProductItemView } from '../../api/generated/models/ContentMasterLocalProductItemView';
import { ContentMasterProductView } from '../../api/generated/models/ContentMasterProductView';
import { MarketplaceProductFieldsView } from '../../api/generated/models/MarketplaceProductFieldsView';
import type { ContentMasterBulkReviewItemView } from '../../api/generated/models/ContentMasterBulkReviewItemView';
import type { ContentMasterBulkReviewListView } from '../../api/generated/models/ContentMasterBulkReviewListView';
import type { ContentMasterBulkSubmitResultView } from '../../api/generated/models/ContentMasterBulkSubmitResultView';
import type { ContentMasterBulkEstimateView } from '../../api/generated/models/ContentMasterBulkEstimateView';
import type { ContentMasterGenerateAllowedOptionView } from '../../api/generated/models/ContentMasterGenerateAllowedOptionView';
import type { ContentMasterGenerateResultView } from '../../api/generated/models/ContentMasterGenerateResultView';
import type { ContentMasterSaveResultView } from '../../api/generated/models/ContentMasterSaveResultView';
import type { ContentMasterTranslateResultView } from '../../api/generated/models/ContentMasterTranslateResultView';
import type { ContentMasterWawiLanguageView } from '../../api/generated/models/ContentMasterWawiLanguageView';
import type { ContentMasterAiTokensSummaryView } from '../../api/generated/models/ContentMasterAiTokensSummaryView';
import type { ContentMasterAiHistoryItemView } from '../../api/generated/models/ContentMasterAiHistoryItemView';
import type { ContentMasterAiHistoryListView } from '../../api/generated/models/ContentMasterAiHistoryListView';
import type { ContentMasterWorkspaceConfigView } from '../../api/generated/models/ContentMasterWorkspaceConfigView';
import {
  isContentMasterChannelKnown,
  pickDefaultContentMasterChannelId,
  readLastSelectedChannelId,
} from '../../utils/contentMasterWorkspace';
import { fetchProfile } from './profileSlice';
import type { RootState } from '../store';

/**
 * Sales channel identifier - composite `id` from
 * `GET /content-master/workspace-config.platforms[].id`. Two shapes:
 *   - `"plattform:N"` (master / marketplace level)
 *   - `"shop:N"`      (per-shop override)
 * Empty string means "no channel selected yet" (initial state before
 * `fetchContentMasterWorkspaceConfig` resolves).
 */
export type ContentMasterSource = string;
/** Empty string = omit `language` (shop default). Otherwise ISO or kSprache id string per API. */
export type ContentMasterListLanguage = string;
export type ContentMasterPostLanguage =
  Parameters<typeof ContentMasterService.postAppContentMasterGenerate>[0]['language'];

export const CONTENT_MASTER_ON_DEMAND_LIMIT_CONFIG = 'content_master_on_demand_limit' as const;
/** Any API field name passed in `POST /content-master/generate#fields` (core, wawiAttr_*, wawiMerkmal_*, wawiMerkmalWert_*). */
export type ContentMasterFieldName = string;
export type ContentMasterGenerateAllowedValues = Record<
  string,
  Array<ContentMasterGenerateAllowedOptionView>
>;
export type ContentMasterDraftFieldsPayload = Parameters<
  typeof ContentMasterService.postAppContentMasterSaveDraft
>[0]['fields'];

/**
 * Empty string = no processingStatus query param (all statuses).
 * Backend accepts comma-separated values from `ContentMasterProductView.processingStatus`,
 * so this is widened to `string` to allow combinations used by the Bulk Review page
 * (e.g. `bulk_generate_queued,bulk_generate_in_progress`).
 */
export type ContentMasterProcessingStatusFilter = string;

/**
 * Status whitelist of the products-list "In Progress" tab. Besides the plain
 * `in_progress` it includes `listing_generate_done`: an SCX listing draft is
 * generated but NOT applied to Wawi yet, so the product still needs rework.
 */
export const CONTENT_MASTER_IN_PROGRESS_TAB_STATUS: ContentMasterProcessingStatusFilter = [
  ContentMasterProductView.processingStatus.IN_PROGRESS,
  ContentMasterProductView.processingStatus.LISTING_GENERATE_DONE,
].join(',');

/** Bulk-only statuses - used by the Bulk Review page filter. */
export const CONTENT_MASTER_BULK_STATUSES: ContentMasterProductView.processingStatus[] = [
  ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED,
  ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS,
  ContentMasterProductView.processingStatus.BULK_GENERATE_DONE,
  ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED,
  ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED,
  ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS,
  ContentMasterProductView.processingStatus.BULK_APPLY_FAILED,
];

/**
 * Active bulk states: a worker is either queued or running. Used both for
 * polling decisions and for disabling per-row action buttons.
 */
export const CONTENT_MASTER_ACTIVE_BULK_STATUSES: ContentMasterProductView.processingStatus[] = [
  ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED,
  ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS,
  ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED,
  ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS,
];

/**
 * One "field → phrase" search filter row (`fieldFilters[]` of
 * `POST /content-master/products/search` and the super-batch routes).
 * `field` keys come from `GET /content-master/filter-fields`.
 */
export interface ContentMasterFieldFilter {
  field: string;
  value: string;
}

/** Backend limits for `fieldFilters` (400 `content_master_invalid_request` beyond them). */
export const CONTENT_MASTER_FIELD_FILTERS_MAX = 10;
export const CONTENT_MASTER_FIELD_FILTER_VALUE_MAX = 200;

export interface ContentMasterListFilters {
  source: ContentMasterSource;
  language: ContentMasterListLanguage;
  page: number;
  limit: number;
  q: string;
  processingStatus: ContentMasterProcessingStatusFilter;
  /**
   * Filter by the Wawi article active flag (`dbo.tArtikel.cAktiv`).
   * `true` (default) → only active products (`active=true` query param);
   * `false` → all products (the param is omitted, so inactive ones show too).
   */
  active: boolean;
  /**
   * Category filter (exact match, no subtree). SCX category id for
   * marketplace channels, Wawi kKategorie otherwise. `null` = no filter.
   */
  kCategory: number | null;
  /**
   * Display name of the selected category - UI only (never sent to the API).
   * Kept alongside `kCategory` so the chip survives URL restore / reload.
   */
  kCategoryName: string | null;
  /**
   * Native Wawi category filter (kKategorie), AND-combined with `kCategory`.
   * Only offered on marketplace channels (elsewhere `kCategory` already IS the
   * Wawi category). `null` = no filter.
   */
  kWawiCategory: number | null;
  /** Display name of the selected Wawi category - UI only (never sent to the API). */
  kWawiCategoryName: string | null;
  /**
   * Include products of ALL descendant categories of `kCategory` /
   * `kWawiCategory` (recursive, applies to both). Only meaningful when at
   * least one of them is set; `false` (default) = exact match.
   */
  includeSubcategories: boolean;
  /** Field → phrase filters, AND-combined (max 10, phrase ≤200 chars). */
  fieldFilters: ContentMasterFieldFilter[];
  /** Only articles with JTL-Wawi stock > 0. Offered on marketplace channels only. */
  onlyInStock: boolean;
  /** Only articles with a non-empty EAN/GTIN in JTL-Wawi. Offered on marketplace channels only. */
  onlyWithEan: boolean;
  /** Only articles NOT activated for the selected marketplace channel (400 on other sources). */
  onlyNotActivated: boolean;
}

/**
 * Page-level tab keys used by the Bulk Review page. NOT a backend status -
 * each tab maps to one or more `ContentMasterProductView.processingStatus`
 * values inside the page's fetch logic. Stored in `bulkReview.filters.tab`.
 */
export type ContentMasterBulkReviewTab = 'generating' | 'applying' | 'ready' | 'errors';

export interface ContentMasterBulkReviewFilters {
  source: ContentMasterSource;
  page: number;
  /** Active tab. Mapped to backend statuses by the page (see ContentMasterBulkReviewPage). */
  tab: ContentMasterBulkReviewTab;
}

interface ContentMasterListState {
  items: ContentMasterProductView[];
  total: number;
  page: number;
  limit: number;
  wawiVersion: string | null;
  processingStatusCounts: Record<string, number>;
  /** Total rows for current search when not filtering by processing status (for "All" tab count while another tab is active). */
  totalWhenUnfiltered: number | null;
}

/**
 * Selection state for bulk operations on the Products list page. Stored as a
 * Set-like array of `wawiArtikelId`s - same identifier the bulk endpoints expect
 * in `items[].wawiArtikelId` (see `POST /v2/api/content-master/bulk/generate`).
 *
 * Selection is intentionally not persisted across page changes / filter changes:
 * the `fetchContentMasterProducts.fulfilled` reducer prunes ids that fell out
 * of the current page so the bottom action bar always reflects what the user
 * can actually see.
 */
interface ContentMasterBulkSelectionState {
  wawiArtikelIds: number[];
}

/**
 * Bulk Review page state. Two list slots so we can render the right shape per
 * tab without runtime discriminated-union checks at every cell:
 *   - `reviewItems` is populated for the "Ready" and "Errors" tabs (the new
 *     `/v2/api/content-master/bulk/review` endpoint payload - has
 *     `languages[<storageCode>].fields[]`, `bulkErrorMessage`, etc.).
 *   - `inProgressItems` is populated for the "In progress" tab (the existing
 *     `/v2/api/content-master/products` listing payload - review endpoint
 *     refuses queued / running statuses on purpose).
 *
 * Both slots share `total` / `page` / `limit` (only one is "active" at a time
 * - see the page's tab effect), which keeps the pagination component simple.
 *
 * `mainImageUrl` lives on the product row and is filled by the bulk-generate
 * worker at first generation, so `/bulk/review` returns it directly - no
 * client-side image cache and no fallback fetch to `/products` is needed.
 * A `null` value (pre-release rows that were never bulk-generated) simply
 * renders the letter-avatar in `ContentMasterProductThumb`.
 */
interface ContentMasterBulkReviewState {
  filters: ContentMasterBulkReviewFilters;
  reviewItems: ContentMasterBulkReviewItemView[];
  inProgressItems: ContentMasterLocalProductItemView[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
}

interface ContentMasterState {
  filters: ContentMasterListFilters;
  list: ContentMasterListState;
  listLoading: boolean;
  listError: string | null;
  detail: ContentMasterProductView | null;
  detailLoading: boolean;
  detailError: string | null;
  generateAllLoading: boolean;
  generateFieldLoading: Partial<Record<string, boolean>>;
  /**
   * Loading flags for the new translation flow. Mirrors `generateAllLoading` /
   * `generateFieldLoading` so the UI can disable buttons / show spinners with
   * the same semantics as Generate. `translateAllLoading` covers Translate-All
   * (header CTA), `translateFieldLoading[fieldKey]` covers per-field Translate.
   */
  translateAllLoading: boolean;
  translateFieldLoading: Partial<Record<string, boolean>>;
  saveDraftLoading: boolean;
  applyLoading: boolean;
  actionError: string | null;
  /** Snapshot from Wawi (languages, defaultLanguage, platforms, dataSourceId). */
  workspaceConfig: ContentMasterWorkspaceConfigView | null;
  wawiLanguages: ContentMasterWawiLanguageView[];
  wawiLanguagesLoading: boolean;
  wawiLanguagesError: string | null;
  aiTokensSummary: ContentMasterAiTokensSummaryView | null;
  aiTokensLoading: boolean;
  aiTokensError: string | null;
  aiHistoryItems: ContentMasterAiHistoryItemView[];
  aiHistoryTotal: number;
  aiHistoryPage: number;
  aiHistoryLimit: number;
  aiHistoryLoading: boolean;
  aiHistoryError: string | null;
  userConfigSaving: boolean;
  userConfigError: string | null;
  /** Sidebar-scope processing status counts (source=jtl, countsOnly=true). */
  menuCounts: Record<string, number> | null;
  menuCountsLoading: boolean;
  menuCountsError: string | null;
  /**
   * Set by the product edit page right before `navigate(-1)` after a successful
   * Save Draft / Apply to Wawi. The list page consumes it on mount to briefly
   * highlight the just-edited row and clears it immediately. Independent from
   * URL params so a hard refresh on the list does not retrigger the highlight.
   */
  pendingHighlightWawiArtikelId: number | null;
  /**
   * Companion to `pendingHighlightWawiArtikelId`: tells the list page to skip
   * exactly one upcoming `fetchContentMasterProducts` call (the one fired on
   * mount). Without this, the back-navigation would refetch the whole list,
   * lose the user's scroll position, and discard the locally-patched status
   * applied via `applyContentMasterListItemStatus`.
   */
  skipNextContentMasterListFetch: boolean;
  /**
   * Visibility flag for the "Not enough tokens" modal shown on the product
   * edit page. Flipped to `true` by the generate / translate thunks when the
   * backend rejects the call with `errorCode === 'content_master_quota_exceeded'`,
   * cleared by the modal's Cancel / View-token-usage buttons.
   *
   * When this flag is set, the matching rejected handlers deliberately leave
   * `actionError` untouched so the user only sees the modal - not the regular
   * error toast.
   */
  quotaExceededModalVisible: boolean;
  /**
   * Upgrade modal for `403 content_master_subscription_insufficient` (Starter
   * plan hitting Pro/Ultimate-only generate / bulk features).
   */
  subscriptionInsufficientModalVisible: boolean;
  /** Row selection on the Products list page (Bulk Generate flow). */
  bulkSelection: ContentMasterBulkSelectionState;
  /** Loading flag for `POST /v2/api/content-master/bulk/generate`. */
  bulkGenerateSubmitting: boolean;
  /** Loading flag for `POST /v2/api/content-master/bulk/translate`. */
  bulkTranslateSubmitting: boolean;
  /**
   * Super-batch ("generate everything matching the filters") state. `estimate`
   * holds the last `POST /bulk/estimate-by-filters` response shown in the
   * confirmation modal; cleared when the modal closes. Errors go through the
   * regular `actionError` toast (403 quota / subscription open the shared
   * modals via the usual sentinels).
   */
  superBatchEstimate: ContentMasterBulkEstimateView | null;
  superBatchEstimateLoading: boolean;
  /** Loading flag for `POST /v2/api/content-master/bulk/generate-by-filters`. */
  superBatchSubmitting: boolean;
  /** Loading flag for `POST /v2/api/content-master/bulk/apply`. */
  bulkApplySubmitting: boolean;
  /**
   * Loading flag for the footer "Retry all" button on the With errors tab -
   * one in-flight `POST /v2/api/content-master/bulk/retry` for every visible
   * row. Per-row spinners use `bulkRetryLoading` instead so the two CTAs can
   * be disabled independently.
   */
  bulkRetrySubmitting: boolean;
  /** Per-product loading flag for the per-row Retry button on the errors tab. */
  bulkRetryLoading: Record<string, boolean>;
  /** Per-product loading flag for `POST /content-master/products/{id}/mark-for-edit`. */
  bulkMarkForEditLoading: Record<string, boolean>;
  /** Bulk Review page state (separate slot so its filters / list don't collide with the Products page). */
  bulkReview: ContentMasterBulkReviewState;
}

/**
 * Seven base product text fields (camelCase API names). `urlPath` is the shop
 * URL slug (`cUrlPfad`) - a normal core field everywhere, EXCEPT that it is
 * listed in `GET /content-master/product.generateDefaultExcludedFields`, so
 * default / select-all generation selections must leave it unchecked.
 */
export const CONTENT_MASTER_CORE_FIELD_KEYS = [
  'title',
  'shortDescription',
  'description',
  'metaTitleTag',
  'metaKeywords',
  'metaDescription',
  'urlPath',
] as const;
export type ContentMasterCoreFieldKey = (typeof CONTENT_MASTER_CORE_FIELD_KEYS)[number];

const editableFields: ContentMasterCoreFieldKey[] = [...CONTENT_MASTER_CORE_FIELD_KEYS];

const initialState: ContentMasterState = {
  filters: {
    source: '',
    language: '',
    page: 1,
    limit: 20,
    q: '',
    processingStatus: '',
    active: true,
    kCategory: null,
    kCategoryName: null,
    kWawiCategory: null,
    kWawiCategoryName: null,
    includeSubcategories: false,
    fieldFilters: [],
    onlyInStock: false,
    onlyWithEan: false,
    onlyNotActivated: false,
  },
  list: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    wawiVersion: null,
    processingStatusCounts: {},
    totalWhenUnfiltered: null,
  },
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  generateAllLoading: false,
  generateFieldLoading: {},
  translateAllLoading: false,
  translateFieldLoading: {},
  saveDraftLoading: false,
  applyLoading: false,
  actionError: null,
  workspaceConfig: null,
  wawiLanguages: [],
  wawiLanguagesLoading: false,
  wawiLanguagesError: null,
  aiTokensSummary: null,
  aiTokensLoading: false,
  aiTokensError: null,
  aiHistoryItems: [],
  aiHistoryTotal: 0,
  aiHistoryPage: 1,
  aiHistoryLimit: 20,
  aiHistoryLoading: false,
  aiHistoryError: null,
  userConfigSaving: false,
  userConfigError: null,
  menuCounts: null,
  menuCountsLoading: false,
  menuCountsError: null,
  pendingHighlightWawiArtikelId: null,
  skipNextContentMasterListFetch: false,
  quotaExceededModalVisible: false,
  subscriptionInsufficientModalVisible: false,
  bulkSelection: {
    wawiArtikelIds: [],
  },
  bulkGenerateSubmitting: false,
  bulkTranslateSubmitting: false,
  superBatchEstimate: null,
  superBatchEstimateLoading: false,
  superBatchSubmitting: false,
  bulkApplySubmitting: false,
  bulkRetrySubmitting: false,
  bulkRetryLoading: {},
  bulkMarkForEditLoading: {},
  bulkReview: {
    filters: {
      source: '',
      page: 1,
      tab: 'ready',
    },
    reviewItems: [],
    inProgressItems: [],
    total: 0,
    page: 1,
    limit: 20,
    loading: false,
    error: null,
  },
};

/**
 * Known `errorCode` values returned by Content Master endpoints
 * (see openapi.yaml - /content-master/generate, /save-draft, /apply, /user-config).
 * Each code has a matching translation key `cm_error.<code>`.
 */
const CONTENT_MASTER_ERROR_CODES = [
  'content_master_invalid_request',
  'content_master_invalid_language',
  'content_master_ai_generation_failed',
  'content_master_wawi_error',
  'content_master_wawi_connection_failed',
  'content_master_field_validation_failed',
  'content_master_wawi_apply_failed',
  'content_master_on_demand_limit_below_usage',
  'content_master_unknown_config',
  'content_master_invalid_config_value',
  'content_master_forbidden',
  'content_master_quota_exceeded',
  'content_master_product_not_found',
  'content_master_bulk_job_already_active',
  'content_master_subscription_insufficient',
] as const;

type ContentMasterErrorCode = (typeof CONTENT_MASTER_ERROR_CODES)[number];

const isContentMasterErrorCode = (code: unknown): code is ContentMasterErrorCode =>
  typeof code === 'string' &&
  (CONTENT_MASTER_ERROR_CODES as readonly string[]).includes(code);

/**
 * Extracts a user-facing message from an API error.
 * Preference order:
 *   1. Localized text for a known Content Master `errorCode`
 *      (`cm_error.<code>`; `{{detail}}` placeholder receives backend `error`).
 *   2. Raw `body.error` returned by the backend.
 *   3. `error.message` / generic fallback.
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as
      | { error?: string; errorCode?: string | null }
      | undefined;
    const detail = typeof body?.error === 'string' ? body.error : '';
    const code = body?.errorCode;
    if (isContentMasterErrorCode(code)) {
      const translated = i18next.t(`cm_error.${code}`, {
        detail,
        defaultValue: detail || error.message,
      });
      if (typeof translated === 'string' && translated.length > 0) {
        return translated;
      }
    }
    if (detail.length > 0) {
      return detail;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return i18next.t('error_unexpected', { defaultValue: 'Unknown error' });
};

const LIST_FETCH_ABORTED = '__LIST_FETCH_ABORTED__';
/**
 * Sentinel rejectWithValue payload used by generate / translate thunks when
 * the backend rejected the request with `errorCode === 'content_master_quota_exceeded'`.
 * The thunks dispatch `setContentMasterQuotaModalVisible(true)` themselves -
 * the rejected reducers detect this sentinel and skip setting `actionError`
 * so the user only sees the modal (not the toast).
 */
const QUOTA_EXCEEDED_SENTINEL = '__CONTENT_MASTER_QUOTA_EXCEEDED__';
const SUBSCRIPTION_INSUFFICIENT_SENTINEL = '__CONTENT_MASTER_SUBSCRIPTION_INSUFFICIENT__';

export const isSilentForbiddenRejection = (payload: unknown): boolean =>
  payload === QUOTA_EXCEEDED_SENTINEL || payload === SUBSCRIPTION_INSUFFICIENT_SENTINEL;

/**
 * Returns true when the underlying API error is a Content Master quota error
 * (`errorCode === 'content_master_quota_exceeded'`). Other errors fall back to
 * the regular toast flow.
 */
const isQuotaExceededError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const body = error.body as { errorCode?: string | null } | undefined;
  return body?.errorCode === 'content_master_quota_exceeded';
};

const isSubscriptionInsufficientError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const body = error.body as { errorCode?: string | null } | undefined;
  return body?.errorCode === 'content_master_subscription_insufficient';
};

const handleContentMasterForbiddenError = (
  error: unknown,
  dispatch: (action: { type: string; payload?: boolean }) => void,
  rejectWithValue: (value: string) => unknown
): unknown | null => {
  if (isQuotaExceededError(error)) {
    dispatch(setContentMasterQuotaModalVisible(true));
    return rejectWithValue(QUOTA_EXCEEDED_SENTINEL);
  }
  if (isSubscriptionInsufficientError(error)) {
    dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
    return rejectWithValue(SUBSCRIPTION_INSUFFICIENT_SENTINEL);
  }
  return null;
};

let activeProductsListRequest: CancelablePromise<ContentMasterProductListView> | null = null;
/**
 * Stable-string signature of the filters of the request currently in flight
 * (or `null` when nothing is in flight). Used by the `condition` callback below
 * to dedupe identical `dispatch(fetchContentMasterProducts(...))` calls - these
 * can otherwise pile up when several effects each react to the same Redux
 * state change and re-dispatch the same filters in the same tick.
 */
let inFlightProductsListSignature: string | null = null;

/**
 * List-fetch argument: the shared filters plus the page-level
 * `includeUnassigned` flag (marketplace sources only - search the WHOLE Wawi
 * catalogue instead of just channel-enrolled articles). The flag deliberately
 * lives OUTSIDE `ContentMasterListFilters`: it is persisted per channel in
 * localStorage by the page, not in the URL.
 */
export type ContentMasterListFetchArgs = ContentMasterListFilters & {
  includeUnassigned?: boolean;
};

/**
 * Rows actually sent to the API: trimmed, complete (field picked AND phrase
 * non-empty), capped at the backend limits. Half-filled rows the user is still
 * editing in the panel are silently ignored.
 */
const normalizedFieldFilters = (
  rows: ContentMasterFieldFilter[]
): ContentMasterFieldFilter[] =>
  rows
    .map((row) => ({ field: row.field, value: row.value.trim() }))
    .filter(
      (row) =>
        row.field !== '' &&
        row.value !== '' &&
        row.value.length <= CONTENT_MASTER_FIELD_FILTER_VALUE_MAX
    )
    .slice(0, CONTENT_MASTER_FIELD_FILTERS_MAX);

/** `fieldFilters` body value: normalized rows, or `undefined` when none. */
const toApiFieldFilters = (
  rows: ContentMasterFieldFilter[]
): ContentMasterFieldFilter[] | undefined => {
  const normalized = normalizedFieldFilters(rows);
  return normalized.length > 0 ? normalized : undefined;
};

/** `includeSubcategories` is only meaningful next to at least one category id. */
const hasCategoryFilter = (f: {
  kCategory?: number | null;
  kWawiCategory?: number | null;
}): boolean => f.kCategory != null || f.kWawiCategory != null;

const buildProductsListSignature = (filters: ContentMasterListFetchArgs): string => {
  return [
    filters.source,
    filters.language.trim(),
    filters.page,
    filters.limit,
    filters.q.trim(),
    filters.processingStatus,
    filters.active ? '1' : '0',
    filters.includeUnassigned ? '1' : '0',
    filters.kCategory != null ? String(filters.kCategory) : '',
    filters.kWawiCategory != null ? String(filters.kWawiCategory) : '',
    hasCategoryFilter(filters) && filters.includeSubcategories ? '1' : '0',
    JSON.stringify(normalizedFieldFilters(filters.fieldFilters)),
    filters.onlyInStock ? '1' : '0',
    filters.onlyWithEan ? '1' : '0',
    filters.onlyNotActivated ? '1' : '0',
  ].join('|');
};

const isAbortLikeError = (error: unknown): boolean => {
  if (error instanceof CancelError) {
    return true;
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }
  return false;
};

export const fetchContentMasterProducts = createAsyncThunk<
  ContentMasterProductListView,
  ContentMasterListFetchArgs,
  { rejectValue: string }
>(
  'contentMaster/fetchProducts',
  async (filters, { rejectWithValue }) => {
    activeProductsListRequest?.cancel();
    const signature = buildProductsListSignature(filters);
    inFlightProductsListSignature = signature;
    // POST twin of GET /products - same response shape, plus the extended
    // `kCategory` / `fieldFilters` filters (query params moved to the body).
    activeProductsListRequest = ContentMasterService.postAppContentMasterProductsSearch({
      source: filters.source,
      page: filters.page,
      limit: filters.limit,
      language: filters.language.trim() || undefined,
      q: filters.q.trim() || undefined,
      processingStatus: filters.processingStatus || undefined,
      active: filters.active ? true : undefined,
      includeUnassigned: filters.includeUnassigned ? true : undefined,
      kCategory: filters.kCategory ?? undefined,
      kWawiCategory: filters.kWawiCategory ?? undefined,
      // Never send `true` without a category (the backend would ignore it,
      // but the contract asks to omit it in that case).
      includeSubcategories:
        hasCategoryFilter(filters) && filters.includeSubcategories ? true : undefined,
      fieldFilters: toApiFieldFilters(filters.fieldFilters),
      onlyInStock: filters.onlyInStock ? true : undefined,
      onlyWithEan: filters.onlyWithEan ? true : undefined,
      onlyNotActivated: filters.onlyNotActivated ? true : undefined,
    });
    try {
      const result = await activeProductsListRequest;
      if (inFlightProductsListSignature === signature) {
        inFlightProductsListSignature = null;
      }
      return result;
    } catch (error) {
      if (inFlightProductsListSignature === signature) {
        inFlightProductsListSignature = null;
      }
      if (isAbortLikeError(error)) {
        return rejectWithValue(LIST_FETCH_ABORTED);
      }
      return rejectWithValue(getErrorMessage(error));
    }
  },
  {
    condition: (filters) => {
      // If a request with the exact same filter signature is already in
      // flight, drop the duplicate dispatch entirely - this is the safety net
      // against the "dozens of cancelled requests in Network" flicker we saw
      // when multiple effects fire in the same tick with identical filters.
      const signature = buildProductsListSignature(filters);
      if (inFlightProductsListSignature === signature) {
        return false;
      }
      return true;
    },
  }
);

export const fetchContentMasterProductDetail = createAsyncThunk<
  ContentMasterProductView,
  { wawiArtikelId: number; source: ContentMasterSource; language?: string; dataSourceId?: string | null },
  { rejectValue: string }
>('contentMaster/fetchProductDetail', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.getAppContentMasterProductDetail(
      params.wawiArtikelId,
      params.source,
      params.dataSourceId?.trim() || undefined,
      params.language?.trim() || undefined
    );
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const generateAllContentMasterFields = createAsyncThunk<
  ContentMasterGenerateResultView,
  {
    wawiArtikelId: number;
    source: ContentMasterSource;
    language: ContentMasterPostLanguage;
    dataSourceId?: string | null;
    /** Required non-empty list of API field names (core, wawiAttr_*, wawiMerkmal_* / wawiMerkmalWert_*). */
    fields: Array<string>;
    /** `{ value, label? }[]` keyed by field API name; see OpenAPI ContentMasterGenerateAllowedOptionView. */
    fieldAllowedValues?: ContentMasterGenerateAllowedValues | null;
    /**
     * User-provided extra product info (free text + client-parsed files),
     * merged into ONE string ≤50000 chars. Single generation only - never
     * stored; omitted when empty.
     */
    additionalContext?: string | null;
  },
  { rejectValue: string }
>('contentMaster/generateAll', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterGenerate({
      wawiArtikelId: params.wawiArtikelId,
      source: params.source,
      language: params.language,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      fields: params.fields,
      fieldAllowedValues: params.fieldAllowedValues ?? undefined,
      additionalContext: params.additionalContext ?? undefined,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Single-field generation uses the same `POST /content-master/generate` endpoint
 * with `fields: [field]`. The legacy `/generate-field` route was removed by the backend.
 */
export const generateContentMasterField = createAsyncThunk<
  ContentMasterGenerateResultView,
  {
    wawiArtikelId: number;
    source: ContentMasterSource;
    language: ContentMasterPostLanguage;
    field: string;
    dataSourceId?: string | null;
    fieldAllowedValues?: ContentMasterGenerateAllowedValues | null;
  },
  { rejectValue: string }
>('contentMaster/generateField', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterGenerate({
      wawiArtikelId: params.wawiArtikelId,
      source: params.source,
      language: params.language,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      fields: [params.field],
      fieldAllowedValues: params.fieldAllowedValues ?? undefined,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Translate one or more fields of a product from `sourceLanguage` to
 * `targetLanguage`. The same thunk powers both per-field translate
 * (`fields=[one]`) and Translate-All (`fields=[many]`) - exactly mirroring the
 * shape of `generateContentMasterField` / `generateAllContentMasterFields`.
 *
 * The backend reads source values directly from Wawi (NOT from the current
 * draft / form) and silently skips field types it cannot translate (numeric /
 * datetime `wawiAttr_*` and any `wawiMerkmal_*` / `wawiMerkmalWert_*`). The UI
 * must therefore drive its post-success update from `result.translated`, not
 * from the request `fields[]`.
 *
 * Caller decides which loading flag to read in the UI: `translateAllLoading`
 * for batch (header CTA), `translateFieldLoading[fieldKey]` for the per-field
 * link. We discriminate via `meta.arg.scope` ('all' | 'field'), since the
 * `fields[]` length is not a reliable signal (single-element batches are
 * legitimate).
 */
export const translateContentMasterFields = createAsyncThunk<
  ContentMasterTranslateResultView,
  {
    wawiArtikelId: number;
    source: ContentMasterSource;
    sourceLanguage: ContentMasterPostLanguage;
    targetLanguage: ContentMasterPostLanguage;
    fields: Array<string>;
    dataSourceId?: string | null;
    /**
     * Drives which loading flag the slice flips:
     *  - `'all'`  → `translateAllLoading`
     *  - `'field'` → `translateFieldLoading[fields[0]]` (single-field call)
     */
    scope: 'all' | 'field';
  },
  { rejectValue: string }
>('contentMaster/translate', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterTranslate({
      wawiArtikelId: params.wawiArtikelId,
      source: params.source,
      sourceLanguage: params.sourceLanguage,
      targetLanguage: params.targetLanguage,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      fields: params.fields,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

export const saveContentMasterDraft = createAsyncThunk<
  ContentMasterSaveResultView,
  {
    wawiArtikelId: number;
    source: ContentMasterSource;
    language: ContentMasterPostLanguage;
    fields: ContentMasterDraftFieldsPayload;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/saveDraft', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterSaveDraft({
      wawiArtikelId: params.wawiArtikelId,
      source: params.source,
      language: params.language,
      fields: params.fields,
      dataSourceId: params.dataSourceId?.trim() || undefined,
    });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const applyContentMasterDraft = createAsyncThunk<
  ContentMasterSaveResultView,
  {
    wawiArtikelId: number;
    source: ContentMasterSource;
    language: ContentMasterPostLanguage;
    fields: ContentMasterDraftFieldsPayload;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/applyDraft', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterApply({
      wawiArtikelId: params.wawiArtikelId,
      source: params.source,
      language: params.language,
      fields: params.fields,
      dataSourceId: params.dataSourceId?.trim() || undefined,
    });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchContentMasterWorkspaceConfig = createAsyncThunk<
  ContentMasterWorkspaceConfigView,
  string | undefined,
  {
    state: {
      contentMaster: ContentMasterState;
      profile: {
        contact?: { parentUserId?: string | null; permissions?: Array<string> };
      };
    };
    rejectValue: string;
  }
>(
  'contentMaster/fetchWorkspaceConfig',
  async (dataSourceId, { rejectWithValue }) => {
    try {
      return await ContentMasterService.getAppContentMasterWorkspaceConfig(dataSourceId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
  {
    // Drop a duplicate dispatch while one is already in flight. Multiple
    // pages (bootstrap, ContentMaster pages, ProductPage on dataSourceId
    // switch) could otherwise collide on the same workspace-config request.
    //
    // Additionally the SINGLE permission gate for every dispatch site: a
    // subuser without content_master_read must never call workspace-config
    // (the backend answers 403 subuser_permission_denied). While the profile
    // has not landed yet we skip as well - during bootstrap the conditional
    // fetch inside fetchUserLicenses can race fetchProfile, and
    // bootstrapSession re-checks after all three settle anyway.
    condition: (_, { getState }) => {
      const state = getState();
      if (state.contentMaster.wawiLanguagesLoading) return false;
      const contact = state.profile.contact;
      if (!contact) return false;
      if (
        typeof contact.parentUserId === 'string' &&
        !(contact.permissions ?? []).includes('content_master_read')
      ) {
        return false;
      }
      return true;
    },
  }
);

export const fetchContentMasterAiTokens = createAsyncThunk<
  ContentMasterAiTokensSummaryView,
  { dateFrom?: string; dateTo?: string } | undefined,
  { rejectValue: string }
>('contentMaster/fetchAiTokens', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.getAppContentMasterAiTokens(params?.dateFrom, params?.dateTo);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchContentMasterAiHistory = createAsyncThunk<
  ContentMasterAiHistoryListView,
  {
    page: number;
    limit: number;
    dataSourceId?: string;
    language?: string;
    source?: ContentMasterSource;
    q?: string;
  },
  { rejectValue: string }
>('contentMaster/fetchAiHistory', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.getAppContentMasterAiHistory(
      params.page,
      params.limit,
      params.dataSourceId,
      params.language?.trim() || undefined,
      params.source,
      params.q?.trim() || undefined
    );
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Lightweight sidebar counter fetch - uses `countsOnly=true` so the backend
 * skips Wawi lookups and returns only `processingStatusCounts`. All other
 * filters are intentionally omitted (ignored by backend in this mode).
 *
 * `source` is REQUIRED: counts are per-Verkaufskanal (independent statuses
 * per `dbo.tShop` row), so the caller MUST pass the channel id resolved from
 * `workspace-config.platforms`. There is no longer a fixed "JTL" fallback.
 */
export const fetchContentMasterMenuCounts = createAsyncThunk<
  Record<string, number>,
  { source: ContentMasterSource; dataSourceId?: string | null },
  { rejectValue: string }
>('contentMaster/fetchMenuCounts', async (params, { rejectWithValue }) => {
  try {
    const result = await ContentMasterService.getAppContentMasterProductsList(
      params.source,
      params.dataSourceId?.trim() || undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      // includeUnassigned slot - regeneration inserted it BEFORE countsOnly;
      // `true` positionally landing here would silently turn the lightweight
      // counts call into a full Wawi catalogue query.
      undefined,
      true
    );
    return result.processingStatusCounts ?? {};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Fetch the Ready / Errors tab content for the Bulk Review page. Uses the
 * dedicated `GET /v2/api/content-master/bulk/review` endpoint which returns
 * one row per product enriched with the per-field original/generated diff per
 * language (`languages` map). The endpoint also returns `bulkErrorMessage` and
 * the canonical product identity.
 *
 * `processingStatus` is the comma-separated whitelist of statuses the
 * endpoint accepts - see `ContentMasterService.getAppContentMasterBulkReview`
 * for the exact list. The page passes either `bulk_generate_done` (Ready
 * tab) or `bulk_generate_failed,bulk_apply_failed` (Errors tab).
 */
let activeBulkReviewRequest: CancelablePromise<ContentMasterBulkReviewListView> | null = null;
export const fetchContentMasterBulkReviewItems = createAsyncThunk<
  ContentMasterBulkReviewListView,
  {
    /** Omit / empty = all channels of the data source (backend `source` is optional). */
    source?: ContentMasterSource;
    /** Omit to load every language that has drafts (recommended for Bulk Review). */
    language?: string;
    page: number;
    limit?: number;
    processingStatus: string;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/fetchBulkReviewItems', async (params, { rejectWithValue }) => {
  activeBulkReviewRequest?.cancel();
  activeBulkReviewRequest = ContentMasterService.getAppContentMasterBulkReview(
    params.source?.trim() || undefined,
    params.language,
    params.dataSourceId?.trim() || undefined,
    params.page,
    params.limit ?? 20,
    params.processingStatus
  );
  try {
    return await activeBulkReviewRequest;
  } catch (error) {
    if (isAbortLikeError(error)) {
      return rejectWithValue(LIST_FETCH_ABORTED);
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Fetch the "In progress" / "Applying" tab content for the Bulk Review page.
 * Uses the DB-only `/products/local` endpoint (source-agnostic, full card
 * fields) so the tabs can show products across ALL channels of the data source
 * when no channel is selected. The review endpoint refuses these active
 * statuses on purpose (drafts may be mid-flight and stale).
 */
let activeBulkReviewInProgressRequest: CancelablePromise<ContentMasterLocalProductListView> | null = null;
export const fetchContentMasterBulkInProgressProducts = createAsyncThunk<
  ContentMasterLocalProductListView,
  {
    /** Omit / empty = all channels of the data source. */
    source?: ContentMasterSource;
    page: number;
    limit?: number;
    dataSourceId?: string | null;
    /** Comma-separated whitelist passed to `processingStatus`. */
    processingStatus: string;
  },
  { rejectValue: string }
>('contentMaster/fetchBulkInProgressProducts', async (params, { rejectWithValue }) => {
  activeBulkReviewInProgressRequest?.cancel();
  const status = params.processingStatus.trim();
  if (!status) {
    return rejectWithValue('content_master_invalid_request');
  }
  activeBulkReviewInProgressRequest = ContentMasterService.getAppContentMasterProductsLocalByStatusList(
    status,
    params.source?.trim() || undefined,
    params.dataSourceId?.trim() || undefined,
    params.page,
    params.limit ?? 20
  );
  try {
    return await activeBulkReviewInProgressRequest;
  } catch (error) {
    if (isAbortLikeError(error)) {
      return rejectWithValue(LIST_FETCH_ABORTED);
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Bulk Generate - `POST /v2/api/content-master/bulk/generate`. Submits N
 * products for asynchronous AI generation. The backend either accepts the
 * whole batch (returning per-item `accepted` / `skipped` arrays) or rejects
 * the request with `403 content_master_quota_exceeded` when the projected
 * token usage exceeds the user's allowance - in that case nothing is enqueued
 * and the regular quota modal is shown to the user.
 */
export const submitContentMasterBulkGenerate = createAsyncThunk<
  ContentMasterBulkSubmitResultView,
  {
    source: ContentMasterSource;
    language: ContentMasterPostLanguage;
    items: Array<{ wawiArtikelId: number }>;
    dataSourceId?: string | null;
    /** Opt-in: also generate the URL path (`urlPath` / `cUrlPfad`). Default false. */
    includeUrlPath?: boolean;
    /**
     * Explicit user choice for fields the AI returns EMPTY (see the OpenAPI
     * contract). Snapshotted in `bulk_config` on the backend, so apply/retry
     * reuse it - it is never sent to /bulk/apply or /bulk/retry.
     */
    emptyFieldPolicy?: ContentMasterEmptyFieldPolicy;
  },
  { rejectValue: string }
>('contentMaster/submitBulkGenerate', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkGenerate({
      source: params.source,
      language: params.language,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      includeUrlPath: params.includeUrlPath || undefined,
      emptyFieldPolicy: params.emptyFieldPolicy,
      items: params.items,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Bulk Translate - `POST /v2/api/content-master/bulk/translate`. Submits N
 * products for asynchronous AI translation from `sourceLanguage` into every
 * entry of `targetLanguages` (JSON body array - never a query string). The
 * pipeline reuses the `bulk_generate_*` statuses; the task kind is exposed as
 * `bulkOperation = translate_all_fields` on the product / review views.
 * Error contract mirrors `submitContentMasterBulkGenerate`: 403 quota /
 * subscription rejections open the shared modals via the sentinels.
 */
export const submitContentMasterBulkTranslate = createAsyncThunk<
  ContentMasterBulkSubmitResultView,
  {
    source: ContentMasterSource;
    sourceLanguage: ContentMasterPostLanguage;
    /** One or more target languages, each different from `sourceLanguage`. */
    targetLanguages: string[];
    items: Array<{ wawiArtikelId: number }>;
    dataSourceId?: string | null;
    /** Opt-in: also translate the URL path (`urlPath` / `cUrlPfad`). Default false. */
    includeUrlPath?: boolean;
  },
  { rejectValue: string }
>('contentMaster/submitBulkTranslate', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkTranslate({
      source: params.source,
      sourceLanguage: params.sourceLanguage,
      targetLanguages: params.targetLanguages,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      includeUrlPath: params.includeUrlPath || undefined,
      items: params.items,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * `emptyFieldPolicy` of `POST /bulk/generate` and `POST /bulk/generate-by-filters`:
 * what to do with fields the AI returns EMPTY (e.g. a picklist attribute where
 * no allowed option fits). Backend default: `keepOriginal`.
 */
export type ContentMasterEmptyFieldPolicy = 'keepOriginal' | 'overwriteEmpty' | 'reject';

/**
 * `listingConfig` of the super-batch routes - same parameters and defaults as
 * `POST /marketplace-listing/generate`. Its PRESENCE switches the super batch
 * to the marketplace-listing flow (SCX channels only); omit it for the
 * classic CM generation.
 */
export interface ContentMasterSuperBatchListingConfig {
  /** Marketplace override; `null` = auto-detect from the channel. */
  marketplace?: 'amazon' | 'otto' | 'kaufland' | 'metro' | 'cdiscount' | 'tiktok' | null;
  generateRequired?: boolean;
  generateRecommended?: boolean;
  generateOther?: boolean;
  autoDetectCategory?: boolean;
  generatePrefilled?: boolean;
  /** "Fill only empty fields". estimate-by-filters ignores it (counts all fields). */
  onlyEmptyFields?: boolean;
}

/**
 * Shared request state of the super-batch routes: the CURRENT search filters
 * (no items list). `POST /bulk/estimate-by-filters` and
 * `POST /bulk/generate-by-filters` accept the exact same body, so the page
 * passes the same object to both thunks (spec: submit what the estimate
 * modal showed).
 */
export interface ContentMasterSuperBatchParams {
  source: ContentMasterSource;
  language: ContentMasterPostLanguage;
  dataSourceId?: string | null;
  kCategory?: number | null;
  /** Wawi category filter - same value the search used (marketplace channels). */
  kWawiCategory?: number | null;
  /** Include the whole subtree of `kCategory` / `kWawiCategory` - same value the search used. */
  includeSubcategories?: boolean | null;
  fieldFilters?: ContentMasterFieldFilter[];
  processingStatus?: string | null;
  q?: string | null;
  active?: boolean | null;
  /** Wawi flags - same values the search used. */
  onlyInStock?: boolean;
  onlyWithEan?: boolean;
  onlyNotActivated?: boolean;
  /**
   * Opt-in: also generate the URL path (`urlPath` / `cUrlPfad`). Default false.
   * MUST be identical for estimate-by-filters and generate-by-filters, or the
   * token estimate diverges (the flag adds one field per product).
   */
  includeUrlPath?: boolean;
  /**
   * Explicit user choice for fields the AI returns EMPTY (classic CM flow
   * only). Sent to generate-by-filters ONLY - estimate-by-filters ignores it
   * (no cost impact), so the page omits it there.
   */
  emptyFieldPolicy?: ContentMasterEmptyFieldPolicy;
  /** Present → marketplace-listing flow; absent/null → classic CM generation. */
  listingConfig?: ContentMasterSuperBatchListingConfig | null;
}

const buildSuperBatchBody = (params: ContentMasterSuperBatchParams) => ({
  source: params.source,
  language: params.language,
  dataSourceId: params.dataSourceId?.trim() || undefined,
  kCategory: params.kCategory ?? undefined,
  kWawiCategory: params.kWawiCategory ?? undefined,
  includeSubcategories:
    hasCategoryFilter(params) && params.includeSubcategories ? true : undefined,
  fieldFilters: params.fieldFilters ? toApiFieldFilters(params.fieldFilters) : undefined,
  processingStatus: params.processingStatus || undefined,
  q: params.q?.trim() || undefined,
  active: params.active ?? undefined,
  onlyInStock: params.onlyInStock || undefined,
  onlyWithEan: params.onlyWithEan || undefined,
  onlyNotActivated: params.onlyNotActivated || undefined,
  includeUrlPath: params.includeUrlPath || undefined,
  emptyFieldPolicy: params.emptyFieldPolicy,
  // Presence of the key = listing mode - never send it for the classic flow.
  listingConfig: params.listingConfig
    ? {
        marketplace: params.listingConfig.marketplace ?? undefined,
        generateRequired: params.listingConfig.generateRequired,
        generateRecommended: params.listingConfig.generateRecommended,
        generateOther: params.listingConfig.generateOther,
        autoDetectCategory: params.listingConfig.autoDetectCategory,
        generatePrefilled: params.listingConfig.generatePrefilled,
        onlyEmptyFields: params.listingConfig.onlyEmptyFields || undefined,
      }
    : undefined,
});

/**
 * Super-batch pre-flight - `POST /v2/api/content-master/bulk/estimate-by-filters`.
 * Resolves how many articles match the filters and the (very rough) token
 * estimate for the confirmation modal. 403 quota / subscription rejections
 * open the shared modals (same contract as the id-based bulk generate).
 */
export const estimateContentMasterSuperBatch = createAsyncThunk<
  ContentMasterBulkEstimateView,
  ContentMasterSuperBatchParams,
  { rejectValue: string }
>('contentMaster/estimateSuperBatch', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkEstimateByFilters(
      buildSuperBatchBody(params)
    );
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Super-batch submit - `POST /v2/api/content-master/bulk/generate-by-filters`.
 * Same body as the estimate. The response mirrors the estimate (computed at
 * submit time) plus `jobId`; there is NO status/cancel endpoint - progress is
 * tracked through the regular processingStatus counts. `wouldExceedBudget` in
 * the response is NOT an error: generation runs until the budget is exhausted.
 */
export const submitContentMasterSuperBatch = createAsyncThunk<
  ContentMasterBulkEstimateView,
  ContentMasterSuperBatchParams,
  { rejectValue: string }
>('contentMaster/submitSuperBatch', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkGenerateByFilters(
      buildSuperBatchBody(params)
    );
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Bulk Apply - `POST /v2/api/content-master/bulk/apply`. Persists the latest
 * draft per field per language to Wawi for every product in `items`. The list
 * of fields is NOT sent: the backend resolves it from the latest draft
 * versions itself.
 */
export const submitContentMasterBulkApply = createAsyncThunk<
  ContentMasterBulkSubmitResultView,
  {
    items: Array<{ contentMasterProductId: string; language: string }>;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/submitBulkApply', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkApply({
      dataSourceId: params.dataSourceId?.trim() || undefined,
      items: params.items,
    });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Bulk Retry - `POST /v2/api/content-master/bulk/retry`. Re-queues products
 * stuck in `bulk_generate_failed` / `bulk_apply_failed`. The backend resolves
 * `source` and `language` from `content_master_product.bulk_config` (snapshot
 * captured at the original submit), so the client only sends the product id.
 * Quota-exceeded behaviour matches `submitContentMasterBulkGenerate`: the
 * shared modal is opened and the rejection is surfaced via the sentinel.
 */
export const submitContentMasterBulkRetry = createAsyncThunk<
  ContentMasterBulkSubmitResultView,
  {
    items?: Array<{ contentMasterProductId: string }>;
    /**
     * Server-side "retry all" mode: the backend collects every product of the
     * user in a retryable `*_failed` status itself; `items` is ignored.
     */
    all?: boolean;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/submitBulkRetry', async (params, { dispatch, rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterBulkRetry({
      dataSourceId: params.dataSourceId?.trim() || undefined,
      all: params.all,
      items: params.items,
    });
  } catch (error) {
    const handled = handleContentMasterForbiddenError(error, dispatch, rejectWithValue);
    if (handled != null) {
      return handled;
    }
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Batch reject - `POST /v2/api/content-master/products/reject`. Moves FAILED
 * products (`bulk_*_failed` / `listing_*_failed`) to `rejected_by_user` and
 * clears `bulk_error_message`; any other status is skipped with `not_failed`.
 * There is no un-reject - the next generate/apply submit re-queues the product.
 */
export const submitContentMasterProductsReject = createAsyncThunk<
  ContentMasterBulkSubmitResultView,
  {
    items: Array<{ contentMasterProductId: string }>;
    dataSourceId?: string | null;
  },
  { rejectValue: string }
>('contentMaster/submitProductsReject', async (params, { rejectWithValue }) => {
  try {
    return await ContentMasterService.postAppContentMasterProductsReject({
      dataSourceId: params.dataSourceId?.trim() || undefined,
      items: params.items,
    });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Mark-for-Edit - `POST /content-master/products/{id}/mark-for-edit`. Pulls a
 * product out of `bulk_*_done` / `bulk_*_failed` back into `in_progress` so
 * the user can continue editing it manually. Refused with 409 while the
 * product is still queued / running on a worker.
 */
export const markContentMasterProductForEdit = createAsyncThunk<
  { contentMasterProductId: string; processingStatus: ContentMasterProductView.processingStatus },
  { contentMasterProductId: string },
  { rejectValue: string }
>('contentMaster/markForEdit', async (params, { rejectWithValue }) => {
  try {
    const result: any = await ContentMasterService.postAppContentMasterProductMarkForEdit(
      params.contentMasterProductId
    );
    return {
      contentMasterProductId:
        (result?.contentMasterProductId as string | undefined) ?? params.contentMasterProductId,
      processingStatus:
        (result?.processingStatus as ContentMasterProductView.processingStatus | undefined) ??
        ContentMasterProductView.processingStatus.IN_PROGRESS,
    };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const putContentMasterUserConfig = createAsyncThunk<
  void,
  { name: typeof CONTENT_MASTER_ON_DEMAND_LIMIT_CONFIG; value: string },
  { state: RootState; rejectValue: string }
>('contentMaster/putUserConfig', async (body, { dispatch, rejectWithValue }) => {
  try {
    await ContentMasterService.putAppContentMasterUserConfig(body);
    // Re-fetch profile (configs) and tokens (onDemandLimitTokens recomputed on backend).
    await Promise.all([
      dispatch(fetchProfile()).unwrap(),
      dispatch(fetchContentMasterAiTokens(undefined)).unwrap(),
    ]);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const contentMasterSlice = createSlice({
  name: 'contentMaster',
  initialState,
  reducers: {
    setContentMasterFilters: (state, action: PayloadAction<Partial<ContentMasterListFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetContentMasterActionError: (state) => {
      state.actionError = null;
    },
    clearContentMasterDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.actionError = null;
    },
    /**
     * Patches the `processingStatus` of a single item in `state.list.items` and
     * keeps `state.list.processingStatusCounts` in sync (so the All / In progress
     * / Done tabs stay consistent without a full list refetch). Used by the
     * product edit page after Save Draft / Apply to Wawi: the backend returns the
     * fresh status in the response, we patch it in place and navigate back.
     */
    applyContentMasterListItemStatus: (
      state,
      action: PayloadAction<{
        wawiArtikelId: number;
        processingStatus: ContentMasterProductView.processingStatus;
      }>
    ) => {
      const { wawiArtikelId, processingStatus } = action.payload;
      const idx = state.list.items.findIndex((it) => it.wawiArtikelId === wawiArtikelId);
      if (idx === -1) {
        return;
      }
      const previousStatus = state.list.items[idx].processingStatus ?? null;
      if (previousStatus === processingStatus) {
        return;
      }
      state.list.items[idx] = {
        ...state.list.items[idx],
        processingStatus,
      };

      // Keep tab counts roughly correct. We can only adjust statuses we've
      // seen counts for; missing keys default to 0 (server might omit zero
      // buckets). totalWhenUnfiltered does not change since the product
      // itself is still in the workspace, only its bucket changed.
      const counts = { ...state.list.processingStatusCounts };
      if (previousStatus) {
        const prevCount = counts[previousStatus] ?? 0;
        counts[previousStatus] = Math.max(0, prevCount - 1);
      }
      counts[processingStatus] = (counts[processingStatus] ?? 0) + 1;
      state.list.processingStatusCounts = counts;
    },
    /**
     * Patches the rendered `title` of a single item in `state.list.items`.
     * Used by the product edit page after a successful Apply when the language
     * the user just persisted matches the language the list is currently
     * showing - keeps the just-edited row in sync without a list refetch
     * (same pattern as `applyContentMasterListItemStatus` for status). No-op
     * when the row is not in the cached list (deep-link from outside the
     * list, list page never visited in this SPA session).
     */
    applyContentMasterListItemTitle: (
      state,
      action: PayloadAction<{ wawiArtikelId: number; title: string }>
    ) => {
      const { wawiArtikelId, title } = action.payload;
      const idx = state.list.items.findIndex((it) => it.wawiArtikelId === wawiArtikelId);
      if (idx === -1) {
        return;
      }
      if (state.list.items[idx].title === title) {
        return;
      }
      state.list.items[idx] = {
        ...state.list.items[idx],
        title,
      };
    },
    /**
     * Patches the `marketplace` block of a single item in `state.list.items`
     * right after a category was assigned through the category picker. A 200
     * from assign-category guarantees the attribute schema is already in Wawi
     * (T7 synchronous marketplace fetch), so the row leaves
     * `category_not_selected` → `ok`. Keeps the list badge and the bulk
     * generate modal's category gate consistent without a full list refetch.
     * No-op when the row is not in the cached list or is not a marketplace
     * row. (`attributeCounts` stays as-is - refreshed on the next list fetch.)
     */
    applyContentMasterListItemMarketplaceCategory: (
      state,
      action: PayloadAction<{ wawiArtikelId: number; kCategory: number; categoryName: string }>
    ) => {
      const { wawiArtikelId, kCategory, categoryName } = action.payload;
      const idx = state.list.items.findIndex((it) => it.wawiArtikelId === wawiArtikelId);
      const marketplace = idx === -1 ? undefined : state.list.items[idx].marketplace;
      if (!marketplace) {
        return;
      }
      state.list.items[idx] = {
        ...state.list.items[idx],
        marketplace: {
          ...marketplace,
          kCategory,
          categoryName,
          status: MarketplaceProductFieldsView.status.OK,
        },
      };
    },
    setPendingContentMasterHighlight: (state, action: PayloadAction<number>) => {
      state.pendingHighlightWawiArtikelId = action.payload;
    },
    clearPendingContentMasterHighlight: (state) => {
      state.pendingHighlightWawiArtikelId = null;
    },
    setSkipNextContentMasterListFetch: (state, action: PayloadAction<boolean>) => {
      state.skipNextContentMasterListFetch = action.payload;
    },
    setContentMasterQuotaModalVisible: (state, action: PayloadAction<boolean>) => {
      state.quotaExceededModalVisible = action.payload;
    },
    setContentMasterSubscriptionInsufficientModalVisible: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subscriptionInsufficientModalVisible = action.payload;
    },
    /**
     * Toggles a single product in the Products-page selection. Idempotent:
     * adds the id when missing, removes it when present. The bottom action
     * bar is rendered when this list is non-empty.
     */
    toggleContentMasterBulkSelection: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      const idx = state.bulkSelection.wawiArtikelIds.indexOf(id);
      if (idx === -1) {
        state.bulkSelection.wawiArtikelIds.push(id);
      } else {
        state.bulkSelection.wawiArtikelIds.splice(idx, 1);
      }
    },
    /**
     * Replaces the current selection with the provided ids (deduplicated).
     * Used by "Select all on page" / explicit set operations.
     */
    setContentMasterBulkSelection: (state, action: PayloadAction<number[]>) => {
      const seen = new Set<number>();
      const result: number[] = [];
      for (const id of action.payload) {
        if (!seen.has(id)) {
          seen.add(id);
          result.push(id);
        }
      }
      state.bulkSelection.wawiArtikelIds = result;
    },
    clearContentMasterBulkSelection: (state) => {
      state.bulkSelection.wawiArtikelIds = [];
    },
    /** Clears the super-batch estimate when the confirmation modal closes. */
    clearContentMasterSuperBatchEstimate: (state) => {
      state.superBatchEstimate = null;
      state.superBatchEstimateLoading = false;
    },
    setContentMasterBulkReviewFilters: (
      state,
      action: PayloadAction<Partial<ContentMasterBulkReviewFilters>>
    ) => {
      state.bulkReview.filters = {
        ...state.bulkReview.filters,
        ...action.payload,
      };
      // Migrate legacy tab key from the previous iteration.
      if ((state.bulkReview.filters.tab as string) === 'in_progress') {
        state.bulkReview.filters.tab = 'generating';
      }
    },
  },
  // ---- end of reducers; extraReducers below ----
  extraReducers: (builder) => {
    builder
      .addCase(fetchContentMasterProducts.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchContentMasterProducts.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list.items = action.payload.items ?? [];
        state.list.total = action.payload.total ?? 0;
        state.list.page = action.payload.page ?? state.filters.page;
        state.list.limit = action.payload.limit ?? state.filters.limit;
        state.list.wawiVersion = action.payload.wawiVersion ?? null;
        state.list.processingStatusCounts = action.payload.processingStatusCounts ?? {};
        if (!action.meta.arg.processingStatus) {
          state.list.totalWhenUnfiltered = action.payload.total ?? 0;
        }
        // Prune the bulk selection - keep only the ids that are still visible
        // on this page. Otherwise the bottom action bar would show "5 selected"
        // for invisible rows after the user paginates / changes filters.
        if (state.bulkSelection.wawiArtikelIds.length > 0) {
          const visibleIds = new Set(
            (action.payload.items ?? [])
              .map((it) => it.wawiArtikelId)
              .filter((v): v is number => typeof v === 'number')
          );
          state.bulkSelection.wawiArtikelIds = state.bulkSelection.wawiArtikelIds.filter((id) =>
            visibleIds.has(id)
          );
        }
      })
      .addCase(fetchContentMasterProducts.rejected, (state, action) => {
        if (action.payload === LIST_FETCH_ABORTED) {
          return;
        }
        state.listLoading = false;
        state.listError = action.payload ?? 'Failed to load product list';
      })
      .addCase(fetchContentMasterProductDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchContentMasterProductDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchContentMasterProductDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload ?? 'Failed to load product details';
      })
      .addCase(generateAllContentMasterFields.pending, (state) => {
        state.generateAllLoading = true;
        state.actionError = null;
      })
      .addCase(generateAllContentMasterFields.fulfilled, (state) => {
        state.generateAllLoading = false;
      })
      .addCase(generateAllContentMasterFields.rejected, (state, action) => {
        state.generateAllLoading = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to generate fields';
      })
      .addCase(generateContentMasterField.pending, (state, action) => {
        state.generateFieldLoading[action.meta.arg.field] = true;
        state.actionError = null;
      })
      .addCase(generateContentMasterField.fulfilled, (state, action) => {
        state.generateFieldLoading[action.meta.arg.field] = false;
      })
      .addCase(generateContentMasterField.rejected, (state, action) => {
        state.generateFieldLoading[action.meta.arg.field] = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to generate field';
      })
      .addCase(translateContentMasterFields.pending, (state, action) => {
        if (action.meta.arg.scope === 'all') {
          state.translateAllLoading = true;
        } else {
          const fieldKey = action.meta.arg.fields[0];
          if (fieldKey) {
            state.translateFieldLoading[fieldKey] = true;
          }
        }
        state.actionError = null;
      })
      .addCase(translateContentMasterFields.fulfilled, (state, action) => {
        if (action.meta.arg.scope === 'all') {
          state.translateAllLoading = false;
        } else {
          const fieldKey = action.meta.arg.fields[0];
          if (fieldKey) {
            state.translateFieldLoading[fieldKey] = false;
          }
        }
      })
      .addCase(translateContentMasterFields.rejected, (state, action) => {
        if (action.meta.arg.scope === 'all') {
          state.translateAllLoading = false;
        } else {
          const fieldKey = action.meta.arg.fields[0];
          if (fieldKey) {
            state.translateFieldLoading[fieldKey] = false;
          }
        }
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to translate fields';
      })
      .addCase(saveContentMasterDraft.pending, (state) => {
        state.saveDraftLoading = true;
        state.actionError = null;
      })
      .addCase(saveContentMasterDraft.fulfilled, (state, action) => {
        state.saveDraftLoading = false;
        if (state.detail && action.payload.processingStatus) {
          state.detail.processingStatus = action.payload.processingStatus;
        }
      })
      .addCase(saveContentMasterDraft.rejected, (state, action) => {
        state.saveDraftLoading = false;
        state.actionError = action.payload ?? 'Failed to save draft';
      })
      .addCase(applyContentMasterDraft.pending, (state) => {
        state.applyLoading = true;
        state.actionError = null;
      })
      .addCase(applyContentMasterDraft.fulfilled, (state, action) => {
        state.applyLoading = false;
        if (state.detail && action.payload.processingStatus) {
          state.detail.processingStatus = action.payload.processingStatus;
        }
      })
      .addCase(applyContentMasterDraft.rejected, (state, action) => {
        state.applyLoading = false;
        state.actionError = action.payload ?? 'Failed to apply draft';
      })
      .addCase(fetchContentMasterWorkspaceConfig.pending, (state) => {
        state.wawiLanguagesLoading = true;
        state.wawiLanguagesError = null;
      })
      .addCase(fetchContentMasterWorkspaceConfig.fulfilled, (state, action) => {
        state.wawiLanguagesLoading = false;
        state.workspaceConfig = action.payload;
        state.wawiLanguages = action.payload.languages ?? [];
        // Seed `filters.source` only when the current value is empty (initial
        // mount) or no longer present in the freshly-loaded workspace
        // snapshot. We deliberately do NOT clobber a valid existing selection
        // - the URL-sync layer in ContentMasterPage already mirrors it back
        // into Redux.
        //
        // Resolution order is the v2 backend prompt's recommendation:
        //   (1) last manually-picked channel for THIS dataSourceId from
        //       localStorage (the spec calls it out explicitly so users
        //       coming back to a multi-shop workspace land where they left).
        //   (2) prioritised default - master JTL-Wawi → Onlineshop default
        //       → first active shop with content → first valid id.
        const platforms = action.payload.platforms;
        if (
          !state.filters.source ||
          !isContentMasterChannelKnown(state.filters.source, platforms)
        ) {
          const persisted = readLastSelectedChannelId(action.payload.dataSourceId);
          if (persisted && isContentMasterChannelKnown(persisted, platforms)) {
            state.filters.source = persisted;
          } else {
            state.filters.source = pickDefaultContentMasterChannelId(platforms) ?? '';
          }
        }
      })
      .addCase(fetchContentMasterWorkspaceConfig.rejected, (state, action) => {
        state.wawiLanguagesLoading = false;
        state.wawiLanguagesError = action.payload ?? 'Failed to load workspace config';
      })
      .addCase(fetchContentMasterAiTokens.pending, (state) => {
        state.aiTokensLoading = true;
        state.aiTokensError = null;
      })
      .addCase(fetchContentMasterAiTokens.fulfilled, (state, action) => {
        state.aiTokensLoading = false;
        state.aiTokensSummary = action.payload;
      })
      .addCase(fetchContentMasterAiTokens.rejected, (state, action) => {
        state.aiTokensLoading = false;
        state.aiTokensError = action.payload ?? 'Failed to load token usage';
      })
      .addCase(fetchContentMasterAiHistory.pending, (state) => {
        state.aiHistoryLoading = true;
        state.aiHistoryError = null;
      })
      .addCase(fetchContentMasterAiHistory.fulfilled, (state, action) => {
        state.aiHistoryLoading = false;
        state.aiHistoryItems = action.payload.items ?? [];
        state.aiHistoryTotal = action.payload.total ?? 0;
        state.aiHistoryPage = action.payload.page ?? action.meta.arg.page;
        state.aiHistoryLimit = action.payload.limit ?? action.meta.arg.limit;
      })
      .addCase(fetchContentMasterAiHistory.rejected, (state, action) => {
        state.aiHistoryLoading = false;
        state.aiHistoryError = action.payload ?? 'Failed to load AI history';
      })
      .addCase(fetchContentMasterMenuCounts.pending, (state) => {
        state.menuCountsLoading = true;
        state.menuCountsError = null;
      })
      .addCase(fetchContentMasterMenuCounts.fulfilled, (state, action) => {
        state.menuCountsLoading = false;
        state.menuCounts = action.payload;
      })
      .addCase(fetchContentMasterMenuCounts.rejected, (state, action) => {
        state.menuCountsLoading = false;
        state.menuCountsError = action.payload ?? 'Failed to load menu counts';
      })
      .addCase(putContentMasterUserConfig.pending, (state) => {
        state.userConfigSaving = true;
        state.userConfigError = null;
      })
      .addCase(putContentMasterUserConfig.fulfilled, (state) => {
        state.userConfigSaving = false;
      })
      .addCase(putContentMasterUserConfig.rejected, (state, action) => {
        state.userConfigSaving = false;
        state.userConfigError = action.payload ?? 'Failed to save setting';
      })
      .addCase(fetchContentMasterBulkReviewItems.pending, (state) => {
        state.bulkReview.loading = true;
        state.bulkReview.error = null;
      })
      .addCase(fetchContentMasterBulkReviewItems.fulfilled, (state, action) => {
        state.bulkReview.loading = false;
        state.bulkReview.reviewItems = action.payload.items ?? [];
        state.bulkReview.inProgressItems = [];
        state.bulkReview.total = action.payload.total ?? 0;
        state.bulkReview.page = action.payload.page ?? state.bulkReview.filters.page;
        state.bulkReview.limit = action.payload.limit ?? state.bulkReview.limit;
      })
      .addCase(fetchContentMasterBulkReviewItems.rejected, (state, action) => {
        if (action.payload === LIST_FETCH_ABORTED) {
          return;
        }
        state.bulkReview.loading = false;
        state.bulkReview.error = action.payload ?? 'Failed to load bulk review list';
      })
      .addCase(fetchContentMasterBulkInProgressProducts.pending, (state) => {
        state.bulkReview.loading = true;
        state.bulkReview.error = null;
      })
      .addCase(fetchContentMasterBulkInProgressProducts.fulfilled, (state, action) => {
        state.bulkReview.loading = false;
        state.bulkReview.reviewItems = [];
        state.bulkReview.inProgressItems = action.payload.items ?? [];
        state.bulkReview.total = action.payload.total ?? 0;
        state.bulkReview.page = action.payload.page ?? state.bulkReview.filters.page;
        state.bulkReview.limit = action.payload.limit ?? state.bulkReview.limit;
      })
      .addCase(fetchContentMasterBulkInProgressProducts.rejected, (state, action) => {
        if (action.payload === LIST_FETCH_ABORTED) {
          return;
        }
        state.bulkReview.loading = false;
        state.bulkReview.error = action.payload ?? 'Failed to load bulk review list';
      })
      .addCase(submitContentMasterBulkGenerate.pending, (state) => {
        state.bulkGenerateSubmitting = true;
        state.actionError = null;
      })
      .addCase(submitContentMasterBulkGenerate.fulfilled, (state, action) => {
        state.bulkGenerateSubmitting = false;
        // Patch list items locally so the user sees `bulk_generate_queued`
        // immediately, before the next list refetch / poll cycle lands.
        const accepted = action.payload.accepted ?? [];
        if (accepted.length > 0) {
          const acceptedByWawiId = new Map<number, ContentMasterProductView.processingStatus>();
          for (const a of accepted) {
            if (typeof a.wawiArtikelId === 'number' && typeof a.processingStatus === 'string') {
              acceptedByWawiId.set(
                a.wawiArtikelId,
                a.processingStatus as ContentMasterProductView.processingStatus
              );
            }
          }
          state.list.items = state.list.items.map((item) => {
            const next = item.wawiArtikelId != null
              ? acceptedByWawiId.get(item.wawiArtikelId)
              : undefined;
            return next ? { ...item, processingStatus: next } : item;
          });
        }
        state.bulkSelection.wawiArtikelIds = [];
      })
      .addCase(submitContentMasterBulkGenerate.rejected, (state, action) => {
        state.bulkGenerateSubmitting = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to submit bulk generation';
      })
      .addCase(submitContentMasterBulkTranslate.pending, (state) => {
        state.bulkTranslateSubmitting = true;
        state.actionError = null;
      })
      .addCase(submitContentMasterBulkTranslate.fulfilled, (state, action) => {
        state.bulkTranslateSubmitting = false;
        // Same local patch as bulk generate: accepted items show
        // `bulk_generate_queued` immediately (translation reuses the statuses).
        const accepted = action.payload.accepted ?? [];
        if (accepted.length > 0) {
          const acceptedByWawiId = new Map<number, ContentMasterProductView.processingStatus>();
          for (const a of accepted) {
            if (typeof a.wawiArtikelId === 'number' && typeof a.processingStatus === 'string') {
              acceptedByWawiId.set(
                a.wawiArtikelId,
                a.processingStatus as ContentMasterProductView.processingStatus
              );
            }
          }
          state.list.items = state.list.items.map((item) => {
            const next = item.wawiArtikelId != null
              ? acceptedByWawiId.get(item.wawiArtikelId)
              : undefined;
            return next
              ? {
                  ...item,
                  processingStatus: next,
                  bulkOperation: ContentMasterProductView.bulkOperation.TRANSLATE_ALL_FIELDS,
                }
              : item;
          });
        }
        state.bulkSelection.wawiArtikelIds = [];
      })
      .addCase(submitContentMasterBulkTranslate.rejected, (state, action) => {
        state.bulkTranslateSubmitting = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to submit bulk translation';
      })
      .addCase(estimateContentMasterSuperBatch.pending, (state) => {
        state.superBatchEstimateLoading = true;
        state.superBatchEstimate = null;
        state.actionError = null;
      })
      .addCase(estimateContentMasterSuperBatch.fulfilled, (state, action) => {
        state.superBatchEstimateLoading = false;
        state.superBatchEstimate = action.payload;
      })
      .addCase(estimateContentMasterSuperBatch.rejected, (state, action) => {
        state.superBatchEstimateLoading = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to estimate the batch';
      })
      .addCase(submitContentMasterSuperBatch.pending, (state) => {
        state.superBatchSubmitting = true;
        state.actionError = null;
      })
      .addCase(submitContentMasterSuperBatch.fulfilled, (state) => {
        state.superBatchSubmitting = false;
        // No local list patch: the filler task queues products asynchronously
        // server-side; statuses arrive with the next list fetch / counts poll.
      })
      .addCase(submitContentMasterSuperBatch.rejected, (state, action) => {
        state.superBatchSubmitting = false;
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to submit the batch';
      })
      // Marketplace listing generate: mirror the classic bulk-generate patch so
      // the Products table shows `listing_generate_queued` immediately (same as
      // Content Master bulk generate), and clear the selection.
      .addCase(submitMarketplaceListingGenerate.fulfilled, (state, action) => {
        const accepted = action.payload.accepted ?? [];
        if (accepted.length > 0) {
          const acceptedByWawiId = new Map<number, ContentMasterProductView.processingStatus>();
          for (const a of accepted) {
            if (typeof a.wawiArtikelId === 'number' && typeof a.processingStatus === 'string') {
              acceptedByWawiId.set(
                a.wawiArtikelId,
                a.processingStatus as ContentMasterProductView.processingStatus
              );
            }
          }
          state.list.items = state.list.items.map((item) => {
            const next =
              item.wawiArtikelId != null ? acceptedByWawiId.get(item.wawiArtikelId) : undefined;
            return next ? { ...item, processingStatus: next } : item;
          });
        }
        state.bulkSelection.wawiArtikelIds = [];
      })
      .addCase(submitContentMasterBulkApply.pending, (state) => {
        state.bulkApplySubmitting = true;
        state.actionError = null;
      })
      .addCase(submitContentMasterBulkApply.fulfilled, (state, action) => {
        state.bulkApplySubmitting = false;
        const accepted = action.payload.accepted ?? [];
        if (accepted.length > 0) {
          const byCmId = new Map<string, ContentMasterProductView.processingStatus>();
          for (const a of accepted) {
            if (typeof a.contentMasterProductId === 'string' && typeof a.processingStatus === 'string') {
              byCmId.set(
                a.contentMasterProductId,
                a.processingStatus as ContentMasterProductView.processingStatus
              );
            }
          }
          state.list.items = state.list.items.map((item) => {
            const next = item.contentMasterProductId
              ? byCmId.get(item.contentMasterProductId)
              : undefined;
            return next ? { ...item, processingStatus: next } : item;
          });
          state.bulkReview.inProgressItems = state.bulkReview.inProgressItems.map((item) => {
            const next = item.contentMasterProductId
              ? byCmId.get(item.contentMasterProductId)
              : undefined;
            return next ? { ...item, processingStatus: next } : item;
          });
          state.bulkReview.reviewItems = state.bulkReview.reviewItems.map((item) => {
            const next = item.contentMasterProductId
              ? byCmId.get(item.contentMasterProductId)
              : undefined;
            return next
              ? {
                  ...item,
                  processingStatus: next as unknown as ContentMasterBulkReviewItemView['processingStatus'],
                }
              : item;
          });
        }
      })
      .addCase(submitContentMasterBulkApply.rejected, (state, action) => {
        state.bulkApplySubmitting = false;
        state.actionError = action.payload ?? 'Failed to submit bulk apply';
      })
      .addCase(submitContentMasterBulkRetry.pending, (state, action) => {
        // The footer "Retry all" submits every visible row (>1 item) or
        // `all: true` (server-side selection, no items); the per-row button
        // submits exactly one. We use that signal to gate the footer button
        // vs. the per-row button independently - both spin when the relevant
        // request is in flight.
        const items = action.meta.arg.items ?? [];
        if (action.meta.arg.all || items.length > 1) {
          state.bulkRetrySubmitting = true;
        }
        for (const item of items) {
          state.bulkRetryLoading[item.contentMasterProductId] = true;
        }
        state.actionError = null;
      })
      .addCase(submitContentMasterBulkRetry.fulfilled, (state, action) => {
        state.bulkRetrySubmitting = false;
        for (const item of action.meta.arg.items ?? []) {
          state.bulkRetryLoading[item.contentMasterProductId] = false;
        }
        const accepted = action.payload.accepted ?? [];
        if (accepted.length === 0) {
          return;
        }
        // Patch statuses for the Products page list and clear bulkErrorMessage
        // for every accepted product (status now `bulk_*_queued`).
        const byCmId = new Map<string, ContentMasterProductView.processingStatus>();
        for (const a of accepted) {
          if (
            typeof a.contentMasterProductId === 'string' &&
            typeof a.processingStatus === 'string'
          ) {
            byCmId.set(
              a.contentMasterProductId,
              a.processingStatus as ContentMasterProductView.processingStatus
            );
          }
        }
        state.list.items = state.list.items.map((item) => {
          const next = item.contentMasterProductId
            ? byCmId.get(item.contentMasterProductId)
            : undefined;
          return next ? { ...item, processingStatus: next, bulkErrorMessage: null } : item;
        });
        // Bulk Review: accepted products transition out of the errors tab
        // (statuses become `bulk_*_queued`), so drop them from `reviewItems`
        // and decrement the running total. The user stays on the errors tab
        // and sees the rows leave one by one - same UX shape as Send-to-Editor.
        const acceptedIds = new Set<string>();
        for (const a of accepted) {
          if (typeof a.contentMasterProductId === 'string') {
            acceptedIds.add(a.contentMasterProductId);
          }
        }
        if (acceptedIds.size > 0) {
          const before = state.bulkReview.reviewItems.length;
          state.bulkReview.reviewItems = state.bulkReview.reviewItems.filter(
            (item) =>
              !item.contentMasterProductId || !acceptedIds.has(item.contentMasterProductId)
          );
          const removed = before - state.bulkReview.reviewItems.length;
          if (removed > 0) {
            state.bulkReview.total = Math.max(0, state.bulkReview.total - removed);
          }
        }
      })
      // Reject shares `bulkRetryLoading` with retry: both are per-row actions
      // on the same failed card, and a row must not run retry and reject at
      // the same time - one in-flight flag per row covers both buttons.
      .addCase(submitContentMasterProductsReject.pending, (state, action) => {
        for (const item of action.meta.arg.items) {
          state.bulkRetryLoading[item.contentMasterProductId] = true;
        }
        state.actionError = null;
      })
      .addCase(submitContentMasterProductsReject.fulfilled, (state, action) => {
        for (const item of action.meta.arg.items) {
          state.bulkRetryLoading[item.contentMasterProductId] = false;
        }
        const accepted = action.payload.accepted ?? [];
        if (accepted.length === 0) {
          return;
        }
        // Products page list: patch status to `rejected_by_user` and clear the
        // error message for every accepted product.
        const byCmId = new Map<string, string>();
        for (const a of accepted) {
          if (
            typeof a.contentMasterProductId === 'string' &&
            typeof a.processingStatus === 'string'
          ) {
            byCmId.set(a.contentMasterProductId, a.processingStatus);
          }
        }
        state.list.items = state.list.items.map((item) => {
          const next = item.contentMasterProductId
            ? byCmId.get(item.contentMasterProductId)
            : undefined;
          return next
            ? {
                ...item,
                processingStatus: next as ContentMasterProductView.processingStatus,
                bulkErrorMessage: null,
              }
            : item;
        });
        // Bulk Review errors tab: rejected products leave the tab - drop them
        // from `reviewItems` and decrement the total (same UX as retry).
        const acceptedIds = new Set<string>();
        for (const a of accepted) {
          if (typeof a.contentMasterProductId === 'string') {
            acceptedIds.add(a.contentMasterProductId);
          }
        }
        if (acceptedIds.size > 0) {
          const before = state.bulkReview.reviewItems.length;
          state.bulkReview.reviewItems = state.bulkReview.reviewItems.filter(
            (item) =>
              !item.contentMasterProductId || !acceptedIds.has(item.contentMasterProductId)
          );
          const removed = before - state.bulkReview.reviewItems.length;
          if (removed > 0) {
            state.bulkReview.total = Math.max(0, state.bulkReview.total - removed);
          }
        }
      })
      .addCase(submitContentMasterProductsReject.rejected, (state, action) => {
        for (const item of action.meta.arg.items) {
          state.bulkRetryLoading[item.contentMasterProductId] = false;
        }
        state.actionError = action.payload ?? 'Failed to reject products';
      })
      .addCase(submitContentMasterBulkRetry.rejected, (state, action) => {
        state.bulkRetrySubmitting = false;
        for (const item of action.meta.arg.items ?? []) {
          state.bulkRetryLoading[item.contentMasterProductId] = false;
        }
        if (isSilentForbiddenRejection(action.payload)) {
          return;
        }
        state.actionError = action.payload ?? 'Failed to submit bulk retry';
      })
      .addCase(markContentMasterProductForEdit.pending, (state, action) => {
        state.bulkMarkForEditLoading[action.meta.arg.contentMasterProductId] = true;
        state.actionError = null;
      })
      .addCase(markContentMasterProductForEdit.fulfilled, (state, action) => {
        state.bulkMarkForEditLoading[action.meta.arg.contentMasterProductId] = false;
        const { contentMasterProductId, processingStatus } = action.payload;
        // Products page list: just patch the status - the row still belongs
        // to that page (`in_progress` is one of its accepted statuses).
        state.list.items = state.list.items.map((item) =>
          item.contentMasterProductId === contentMasterProductId
            ? { ...item, processingStatus, bulkErrorMessage: null }
            : item
        );
        // Bulk Review page: the product no longer matches any of the three
        // tabs' status whitelists - drop it from both list slots so the
        // active page renders without it (and `total` stays consistent for
        // pagination). The footer "N products" caption updates immediately.
        const beforeReview = state.bulkReview.reviewItems.length;
        const beforeInProgress = state.bulkReview.inProgressItems.length;
        state.bulkReview.reviewItems = state.bulkReview.reviewItems.filter(
          (item) => item.contentMasterProductId !== contentMasterProductId
        );
        state.bulkReview.inProgressItems = state.bulkReview.inProgressItems.filter(
          (item) => item.contentMasterProductId !== contentMasterProductId
        );
        const removed =
          beforeReview - state.bulkReview.reviewItems.length +
          beforeInProgress - state.bulkReview.inProgressItems.length;
        if (removed > 0) {
          state.bulkReview.total = Math.max(0, state.bulkReview.total - removed);
        }
      })
      .addCase(markContentMasterProductForEdit.rejected, (state, action) => {
        state.bulkMarkForEditLoading[action.meta.arg.contentMasterProductId] = false;
        state.actionError = action.payload ?? 'Failed to mark product for edit';
      });
  },
});

export const {
  setContentMasterFilters,
  resetContentMasterActionError,
  clearContentMasterDetail,
  applyContentMasterListItemStatus,
  applyContentMasterListItemTitle,
  applyContentMasterListItemMarketplaceCategory,
  setPendingContentMasterHighlight,
  clearPendingContentMasterHighlight,
  setSkipNextContentMasterListFetch,
  setContentMasterQuotaModalVisible,
  setContentMasterSubscriptionInsufficientModalVisible,
  toggleContentMasterBulkSelection,
  setContentMasterBulkSelection,
  clearContentMasterBulkSelection,
  clearContentMasterSuperBatchEstimate,
  setContentMasterBulkReviewFilters,
} = contentMasterSlice.actions;

export { editableFields };
export default contentMasterSlice.reducer;
